import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Modal, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  LogoutOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  NotificationOutlined,
  ControlOutlined,
} from '@ant-design/icons';
import { useUserStore } from '@/stores/userStore';
import BroadcastInbox from '@/components/broadcast/BroadcastInbox';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

function BasicLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { userInfo, logout } = useUserStore();

  const menuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '工作台',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '用户管理',
    },
    {
      key: '/roles',
      icon: <TeamOutlined />,
      label: '角色权限',
    },
  ];

  const isAdmin = useUserStore((s) => s.isAdmin);

  const userMenuItems: MenuProps['items'] = [
    ...(isAdmin
      ? [
          {
            key: 'control-center',
            icon: <ControlOutlined />,
            label: '星舰控制中心',
          },
          {
            key: 'broadcast-admin',
            icon: <NotificationOutlined />,
            label: '星舰广播中心',
          },
          {
            key: 'crew-management',
            icon: <TeamOutlined />,
            label: '星舰机组管理',
          },
          { type: 'divider' as const },
        ]
      : []),
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '个人设置',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      Modal.confirm({
        title: '确认退出',
        icon: <ExclamationCircleOutlined />,
        content: '确定要退出登录吗?',
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          logout();
          message.success('已退出登录');
          navigate('/login', { replace: true });
        },
      });
    } else if (key === 'settings') {
      message.info('个人设置功能开发中');
    } else if (key === 'broadcast-admin') {
      navigate('/broadcast-admin');
    } else if (key === 'control-center') {
      navigate('/control-center');
    } else if (key === 'crew-management') {
      navigate('/crew-management');
    }
  };

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (!userInfo) return '未登录';
    return userInfo.realName || userInfo.username || userInfo.email || userInfo.id;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 20,
          fontWeight: 'bold',
        }}>
          {collapsed ? 'AC' : 'AdenCockpit'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 500 }}>
            伊敦驾驶舱
          </div>
          <Space size="middle">
            <BroadcastInbox />
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }}>
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <Text>{getUserDisplayName()}</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

export default BasicLayout;
